// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import Full120Chart from "./Full120Chart/Full120Chart";
import Full120ChartLegend from "./Full120ChartLegend/Full120ChartLegend";
import GazeGraph from "../../../GazeGraph/GazeGraph";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import {
  getChartSpots,
  getGazeChartData,
  isTerminatedExamReport,
} from "src/utils/exams/examReportsHelper";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

// Constants
import { examCardEnums } from "src/constants/enums";

const Full120Graph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.VISUAL_FIELD_FULL_120.sectionName,
      }),
    [exam, examReport]
  );

  const dimensions = useComponentDimensions({
    componentId: "#full120-container",
  });

  const full120Spots = useMemo(
    () => getChartSpots(examReport) ?? [],
    [examReport]
  );

  const gazeSpots = useMemo(() => {
    const { gazeSpots } = getGazeChartData(examReport);
    return gazeSpots ?? [];
  }, [examReport]);

  return (
    <Grid container gap="20px" id="full120-container">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt="20px"
      >
        <Full120Chart
          full120Spots={full120Spots}
          containerSize={dimensions?.dynamicWidth}
        />
      </Grid>
      <Grid item xs={12}>
        <Full120ChartLegend full120Spots={full120Spots} />
      </Grid>
      <Grid item xs={12}>
        <GazeGraph gazeSpots={gazeSpots} />
      </Grid>
    </Grid>
  );
};

export default Full120Graph;
